import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Jarticard, JarticardType } from '@/lib/types';
import { JARTICARD_TYPE_LABELS } from '@/lib/types';
import { JarticardFrame, JARTICARD_STYLES } from './JarticardFrame';
import { Button } from '@/components/ui/button';
import { ChevronRight, Sparkles } from 'lucide-react';

/** Rareza por tipo: controla la intensidad del revelado. */
const RARITY: Record<JarticardType, number> = {
  recuerdos: 1, momentos: 1, banda: 2, iglesia: 2, escudos: 2, titular: 3,
  paso_cristo: 3, paso_virgen: 3, estandarte: 4, prime: 5, superprime: 6,
};

const RARITY_LABEL: Record<JarticardType, string | null> = {
  recuerdos: null,
  momentos: null,
  banda: '¡BANDA!',
  iglesia: '⛪ IGLESIA',
  escudos: '⚜ ESCUDOS ⚜',
  titular: '✦ TITULAR ✦',
  paso_cristo: '✝ PASO CRISTO ✝',
  paso_virgen: '♛ PASO VIRGEN ♛',
  estandarte: '★ ESTANDARTE ★',
  prime: '✦✦ PRIME ✦✦',
  superprime: '✦✦✦ SÚPER PRIME ✦✦✦',
};

const RAINBOW = ['#f43f5e', '#f59e0b', '#fde047', '#4ade80', '#38bdf8', '#a855f7', '#ec4899'];
const CRYSTAL = ['#f0abfc', '#67e8f9', '#fde047', '#86efac', '#a5b4fc', '#ffffff'];

// ── Sonido procedural (escala con la rareza) ──
function playReveal(rarity: number) {
  try {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    const ac = new AC();
    const now = ac.currentTime;
    const master = ac.createGain();
    master.gain.value = 0.18;
    master.connect(ac.destination);

    const scale = [261.63, 329.63, 392.0, 523.25, 659.25, 783.99, 1046.5];
    const notes = scale.slice(0, Math.min(scale.length, rarity + 1));
    notes.forEach((f, i) => {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = rarity >= 4 ? 'sawtooth' : 'triangle';
      o.frequency.value = f;
      const t = now + i * 0.07;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.5, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
      o.connect(g); g.connect(master);
      o.start(t); o.stop(t + 0.5);
    });

    if (rarity >= 5) {
      // Barrido ascendente épico + campana brillante para PRIME
      const sweep = ac.createOscillator();
      const sg = ac.createGain();
      sweep.type = 'sawtooth';
      sweep.frequency.setValueAtTime(110, now);
      sweep.frequency.exponentialRampToValueAtTime(1760, now + 1.1);
      sg.gain.setValueAtTime(0.0001, now);
      sg.gain.exponentialRampToValueAtTime(0.35, now + 0.6);
      sg.gain.exponentialRampToValueAtTime(0.0001, now + 1.3);
      sweep.connect(sg); sg.connect(master);
      sweep.start(now); sweep.stop(now + 1.3);

      const bell = ac.createOscillator();
      const bg = ac.createGain();
      bell.type = 'sine';
      bell.frequency.value = 2093;
      bg.gain.setValueAtTime(0.0001, now + 1.0);
      bg.gain.exponentialRampToValueAtTime(0.4, now + 1.05);
      bg.gain.exponentialRampToValueAtTime(0.0001, now + 2.0);
      bell.connect(bg); bg.connect(master);
      bell.start(now + 1.0); bell.stop(now + 2.0);
    }

    setTimeout(() => ac.close().catch(() => {}), 2500);
  } catch {
    // El audio es un extra; si falla, no pasa nada.
  }
}

// Banda sonora cinematográfica exclusiva de SÚPER PRIME (de otro planeta)
function playSuperPrime() {
  try {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    const ac = new AC();
    const now = ac.currentTime;
    const master = ac.createGain();
    master.gain.value = 0.22;
    master.connect(ac.destination);

    // 1) Sub-grave que asciende (riser de tensión)
    const sub = ac.createOscillator();
    const sg = ac.createGain();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(38, now);
    sub.frequency.exponentialRampToValueAtTime(180, now + 1.5);
    sg.gain.setValueAtTime(0.0001, now);
    sg.gain.exponentialRampToValueAtTime(0.55, now + 1.35);
    sg.gain.exponentialRampToValueAtTime(0.0001, now + 2.2);
    sub.connect(sg); sg.connect(master);
    sub.start(now); sub.stop(now + 2.3);

    // 2) Arpegio shimmer ascendente
    [523.25, 659.25, 783.99, 1046.5, 1318.5, 1567.98, 2093].forEach((f, i) => {
      const o = ac.createOscillator(); const g = ac.createGain();
      o.type = 'triangle'; o.frequency.value = f;
      const t = now + 0.15 + i * 0.09;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.32, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.6);
      o.connect(g); g.connect(master); o.start(t); o.stop(t + 0.65);
    });

    // 3) Acorde mayor triunfal (el "impacto" del reveal)
    [130.81, 261.63, 329.63, 392.0, 523.25, 659.25].forEach((f) => {
      const o = ac.createOscillator(); const g = ac.createGain();
      o.type = 'sawtooth'; o.frequency.value = f;
      const t = now + 1.2;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.4, t + 0.05);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 1.8);
      o.connect(g); g.connect(master); o.start(t); o.stop(t + 1.9);
    });

    // 4) Cascada de campanas brillantes
    [2093, 2637, 3136, 3520].forEach((f, i) => {
      const o = ac.createOscillator(); const g = ac.createGain();
      o.type = 'sine'; o.frequency.value = f;
      const t = now + 1.3 + i * 0.12;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.28, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 1.3);
      o.connect(g); g.connect(master); o.start(t); o.stop(t + 1.4);
    });

    setTimeout(() => ac.close().catch(() => {}), 3400);
  } catch {
    // El audio es un extra; si falla, no pasa nada.
  }
}

interface Particle { id: number; x: number; y: number; size: number; color: string; dur: number; delay: number; }

function makeParticles(type: JarticardType, count: number): Particle[] {
  const style = JARTICARD_STYLES[type];
  const epic = type === 'prime' || type === 'superprime';
  const palette = type === 'superprime' ? CRYSTAL : RAINBOW;
  return Array.from({ length: count }, (_, i) => {
    const angle = Math.random() * Math.PI * 2;
    const dist = 120 + Math.random() * (type === 'superprime' ? 460 : 320);
    const color = epic ? palette[i % palette.length] : (Math.random() > 0.5 ? style.frame : style.glow);
    return {
      id: i,
      x: Math.cos(angle) * dist,
      y: Math.sin(angle) * dist,
      size: 4 + Math.random() * (epic ? 12 : 7),
      color,
      dur: 0.7 + Math.random() * 0.9,
      delay: Math.random() * 0.18,
    };
  });
}

export function SobreReveal({ cards, onClose }: { cards: Jarticard[]; onClose: () => void }) {
  const [index, setIndex] = useState(0);
  const [done, setDone] = useState(false);

  const card = cards[index];
  const rarity = card ? RARITY[card.type] : 1;
  const style = card ? JARTICARD_STYLES[card.type] : JARTICARD_STYLES.titular;
  const isPrime = card?.type === 'prime';
  const isSuper = card?.type === 'superprime';
  const isEpic = isPrime || isSuper;

  const particles = useMemo(
    () => (card ? makeParticles(card.type, [0, 8, 12, 20, 30, 70, 120][rarity] || 12) : []),
    [card, rarity],
  );

  // Esquirlas de cristal que estallan (solo SÚPER PRIME)
  const shards = useMemo(
    () => (isSuper
      ? Array.from({ length: 16 }, (_, i) => {
          const ang = (i / 16) * Math.PI * 2 + Math.random() * 0.3;
          const dist = 220 + Math.random() * 300;
          return {
            id: i,
            x: Math.cos(ang) * dist,
            y: Math.sin(ang) * dist,
            rot: Math.random() * 720 - 360,
            size: 20 + Math.random() * 30,
            color: CRYSTAL[i % CRYSTAL.length],
            dur: 0.9 + Math.random() * 0.8,
          };
        })
      : []),
    [index, isSuper],
  );

  // Líneas de hiperespacio (warp) para SÚPER PRIME
  const warp = useMemo(
    () => (isSuper
      ? Array.from({ length: 56 }, (_, i) => ({ id: i, ang: (i / 56) * 360, len: 70 + Math.random() * 160, delay: Math.random() * 0.2 }))
      : []),
    [index, isSuper],
  );

  // Sonido al revelar cada carta (la Súper Prime tiene su propia banda sonora)
  useEffect(() => {
    if (!card || done) return;
    if (card.type === 'superprime') playSuperPrime();
    else playReveal(rarity);
  }, [index, done]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = () => {
    if (index < cards.length - 1) setIndex((i) => i + 1);
    else setDone(true);
  };

  if (cards.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[120] flex flex-col items-center justify-center overflow-hidden bg-black/90 backdrop-blur-sm"
    >
      {!done ? (
        <>
          {/* Fondo dinámico según rareza */}
          <motion.div
            key={`bg-${index}`}
            className="pointer-events-none absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ background: `radial-gradient(circle at 50% 45%, ${style.glow}${isEpic ? '55' : '33'}, transparent 60%)` }}
          />

          {/* Rayos giratorios (rareza >= 3) */}
          {rarity >= 3 && (
            <motion.div
              key={`rays-${index}`}
              className="pointer-events-none absolute left-1/2 top-[45%] h-[160vmax] w-[160vmax] -translate-x-1/2 -translate-y-1/2"
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: isSuper ? 4 : isPrime ? 7 : 16, ease: 'linear' }}
              style={{
                opacity: isSuper ? 0.65 : isPrime ? 0.5 : 0.22,
                background: isEpic
                  ? `conic-gradient(${(isSuper ? CRYSTAL : RAINBOW).map((c, i) => `${c} ${i * (360 / (isSuper ? CRYSTAL : RAINBOW).length)}deg`).join(', ')}, ${(isSuper ? CRYSTAL : RAINBOW)[0]} 360deg)`
                  : `repeating-conic-gradient(${style.glow} 0deg 6deg, transparent 6deg 18deg)`,
                maskImage: 'radial-gradient(circle, transparent 18%, black 30%, transparent 70%)',
                WebkitMaskImage: 'radial-gradient(circle, transparent 18%, black 30%, transparent 70%)',
              }}
            />
          )}

          {/* Segundos rayos (contra-rotación) solo SÚPER PRIME */}
          {isSuper && (
            <motion.div
              key={`rays2-${index}`}
              className="pointer-events-none absolute left-1/2 top-[45%] h-[140vmax] w-[140vmax] -translate-x-1/2 -translate-y-1/2"
              animate={{ rotate: -360 }}
              transition={{ repeat: Infinity, duration: 9, ease: 'linear' }}
              style={{
                opacity: 0.4,
                background: 'repeating-conic-gradient(#ffffff 0deg 2deg, transparent 2deg 14deg)',
                maskImage: 'radial-gradient(circle, transparent 22%, black 34%, transparent 64%)',
                WebkitMaskImage: 'radial-gradient(circle, transparent 22%, black 34%, transparent 64%)',
              }}
            />
          )}

          {/* Flash inicial (PRIME y SÚPER PRIME) */}
          {isEpic && (
            <motion.div
              key={`flash-${index}`}
              className="pointer-events-none absolute inset-0 bg-white"
              initial={{ opacity: isSuper ? 1 : 0.95 }}
              animate={{ opacity: 0 }}
              transition={{ duration: isSuper ? 0.85 : 0.6, ease: 'easeOut' }}
            />
          )}

          {/* Segundo flash cian para SÚPER PRIME */}
          {isSuper && (
            <motion.div
              key={`flash2-${index}`}
              className="pointer-events-none absolute inset-0"
              style={{ background: 'radial-gradient(circle, #a5f3fc, transparent 65%)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.9, 0] }}
              transition={{ duration: 0.7, delay: 0.5, ease: 'easeOut' }}
            />
          )}

          {/* Warp de hiperespacio + ondas expansivas (SÚPER PRIME) */}
          {isSuper && (
            <>
              <div className="pointer-events-none absolute left-1/2 top-[45%] z-[5]">
                {warp.map((w) => (
                  <motion.span
                    key={`${index}-warp-${w.id}`}
                    className="absolute left-0 top-0 origin-top rounded-full"
                    style={{
                      width: 2.5,
                      background: 'linear-gradient(to bottom, rgba(255,255,255,0.95), transparent)',
                      transform: `rotate(${w.ang}deg)`,
                    }}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: [0, w.len * 6, w.len * 6], opacity: [0, 1, 0] }}
                    transition={{ duration: 1.1, delay: 0.5 + w.delay, ease: 'easeOut' }}
                  />
                ))}
              </div>
              {[0, 0.25, 0.5].map((d, i) => (
                <motion.div
                  key={`${index}-shock-${i}`}
                  className="pointer-events-none absolute left-1/2 top-[45%] rounded-full border-2"
                  style={{ borderColor: 'rgba(165,243,252,0.85)', boxShadow: '0 0 30px rgba(165,243,252,0.6)' }}
                  initial={{ width: 0, height: 0, x: '-50%', y: '-50%', opacity: 0.9 }}
                  animate={{ width: '200vmax', height: '200vmax', opacity: 0 }}
                  transition={{ duration: 1.5, delay: 0.5 + d, ease: 'easeOut' }}
                />
              ))}
            </>
          )}

          {/* Viñeta pulsante (PRIME / SÚPER PRIME) */}
          {isEpic && (
            <motion.div
              className="pointer-events-none absolute inset-0"
              animate={{ opacity: [0.3, 0.6, 0.3] }}
              transition={{ repeat: Infinity, duration: 1.6 }}
              style={{ boxShadow: isSuper ? 'inset 0 0 220px 70px rgba(103,232,249,0.55)' : 'inset 0 0 200px 60px rgba(168,85,247,0.5)' }}
            />
          )}

          {/* Escenario central */}
          <div className="relative z-10 flex flex-col items-center gap-6 px-4">
            {/* Partículas */}
            <div className="pointer-events-none absolute left-1/2 top-1/2">
              <AnimatePresence>
                {particles.map((p) => (
                  <motion.span
                    key={`${index}-${p.id}`}
                    className="absolute rounded-full"
                    style={{ width: p.size, height: p.size, background: p.color, boxShadow: `0 0 8px ${p.color}` }}
                    initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                    animate={{ x: p.x, y: p.y, opacity: 0, scale: 0.2 }}
                    transition={{ duration: p.dur, delay: p.delay, ease: 'easeOut' }}
                  />
                ))}
              </AnimatePresence>
            </div>

            {/* Esquirlas de cristal (SÚPER PRIME) */}
            {isSuper && (
              <div className="pointer-events-none absolute left-1/2 top-1/2">
                {shards.map((s) => (
                  <motion.span
                    key={`${index}-shard-${s.id}`}
                    className="absolute"
                    style={{
                      width: s.size,
                      height: s.size * 1.8,
                      background: `linear-gradient(180deg, #ffffff, ${s.color})`,
                      clipPath: 'polygon(50% 0%, 62% 70%, 50% 100%, 38% 70%)',
                      filter: `drop-shadow(0 0 8px ${s.color})`,
                    }}
                    initial={{ x: 0, y: 0, opacity: 1, scale: 0.4, rotate: 0 }}
                    animate={{ x: s.x, y: s.y, opacity: 0, scale: 1.2, rotate: s.rot }}
                    transition={{ duration: s.dur, delay: 0.5 + Math.random() * 0.2, ease: 'easeOut' }}
                  />
                ))}
              </div>
            )}

            {/* Carta (entrada según tipo + temblor según rareza) */}
            <motion.div
              key={`shake-${index}`}
              animate={{ x: isSuper ? [0, -18, 18, -14, 14, -8, 8, -4, 4, 0] : rarity >= 3 ? [0, -10, 10, -7, 7, -3, 3, 0] : 0 }}
              transition={{ duration: isSuper ? 0.7 : 0.5, delay: isSuper ? 0.9 : isPrime ? 0.55 : 0.15 }}
            >
              <CardEntrance card={card} type={card.type} rarity={rarity} />
            </motion.div>

            {/* Banner de rareza */}
            <AnimatePresence>
              {RARITY_LABEL[card.type] && (
                <motion.div
                  key={`label-${index}`}
                  initial={{ scale: 0, opacity: 0, rotate: -8 }}
                  animate={{ scale: 1, opacity: 1, rotate: 0 }}
                  transition={{ delay: isSuper ? 1.1 : isPrime ? 0.8 : 0.35, type: 'spring', stiffness: 300, damping: 14 }}
                  className="relative"
                >
                  <span
                    className="block rounded-full px-6 py-2 text-xl font-black uppercase tracking-[0.2em] drop-shadow-lg sm:text-2xl"
                    style={{
                      color: isSuper ? '#06303b' : '#fff',
                      background: isSuper
                        ? `linear-gradient(90deg, ${CRYSTAL.join(', ')})`
                        : isPrime
                          ? `linear-gradient(90deg, ${RAINBOW.join(', ')})`
                          : `linear-gradient(90deg, ${style.frame}, ${style.frame2})`,
                      backgroundSize: isEpic ? '300% 100%' : '100%',
                      animation: isEpic ? 'jc-rainbow 2s linear infinite' : undefined,
                    }}
                  >
                    {RARITY_LABEL[card.type]}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            <p className="text-center text-sm text-white/70">
              {card.name} · #{card.number} · {JARTICARD_TYPE_LABELS[card.type]}
            </p>
          </div>

          {/* Controles */}
          <div className="absolute bottom-8 z-20 flex flex-col items-center gap-3">
            <div className="flex gap-1.5">
              {cards.map((_, i) => (
                <span key={i} className={`h-2 rounded-full transition-all ${i === index ? 'w-6 bg-white' : i < index ? 'w-2 bg-white/60' : 'w-2 bg-white/25'}`} />
              ))}
            </div>
            <Button size="lg" onClick={next} className="min-w-44">
              {index < cards.length - 1 ? <>Siguiente <ChevronRight className="h-4 w-4" /></> : <>Ver resumen <Sparkles className="h-4 w-4" /></>}
            </Button>
          </div>

          {/* Click en el fondo también avanza */}
          <button aria-label="Siguiente" onClick={next} className="absolute inset-0 z-0 cursor-pointer" />
        </>
      ) : (
        // Resumen final
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 w-full max-w-3xl px-6"
        >
          <h2 className="mb-6 text-center font-headline text-3xl font-bold text-white">
            <Sparkles className="mr-2 inline h-7 w-7 text-amber-400" /> ¡Has conseguido!
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {cards.map((c, i) => (
              <motion.div
                key={`${c.id}-${i}`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.06 }}
              >
                <JarticardFrame card={c} owned />
              </motion.div>
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Button size="lg" onClick={onClose}>¡Genial!</Button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

// ── Entrada de la carta según su tipo ──

function CardEntrance({ card, type, rarity }: { card: Jarticard; type: JarticardType; rarity: number }) {
  const isPrime = type === 'prime';
  const isSuper = type === 'superprime';
  const size = isSuper ? 'w-72 sm:w-80 md:w-96' : isPrime ? 'w-72 sm:w-80' : rarity >= 4 ? 'w-64 sm:w-72' : 'w-56 sm:w-64';

  // Variante de entrada distinta por tipo
  const variants: Record<JarticardType, { initial: any; animate: any; transition: any }> = {
    recuerdos: {
      initial: { opacity: 0, y: 30, filter: 'sepia(1) blur(6px)' },
      animate: { opacity: 1, y: 0, filter: 'sepia(0) blur(0px)' },
      transition: { duration: 0.7, ease: 'easeOut' },
    },
    momentos: {
      initial: { opacity: 0, scale: 0.7 },
      animate: { opacity: 1, scale: [0.7, 1.1, 1] },
      transition: { duration: 0.7, ease: 'easeOut' },
    },
    banda: {
      initial: { opacity: 0, rotateY: 90, x: -120 },
      animate: { opacity: 1, rotateY: 0, x: 0 },
      transition: { duration: 0.6, ease: 'easeOut' },
    },
    titular: {
      initial: { opacity: 0, rotateY: 180, scale: 0.8 },
      animate: { opacity: 1, rotateY: 0, scale: 1 },
      transition: { duration: 0.7, type: 'spring', stiffness: 120 },
    },
    estandarte: {
      initial: { opacity: 0, y: 160, scale: 0.6, rotate: -6 },
      animate: { opacity: 1, y: 0, scale: 1, rotate: 0 },
      transition: { duration: 0.8, type: 'spring', stiffness: 90, damping: 12 },
    },
    iglesia: {
      initial: { opacity: 0, y: 40, scale: 0.85 },
      animate: { opacity: 1, y: 0, scale: 1 },
      transition: { duration: 0.7, ease: 'easeOut' },
    },
    paso_cristo: {
      initial: { opacity: 0, y: 80, scale: 0.7, filter: 'brightness(2)' },
      animate: { opacity: 1, y: 0, scale: 1, filter: 'brightness(1)' },
      transition: { duration: 0.8, type: 'spring', stiffness: 100, damping: 13 },
    },
    paso_virgen: {
      initial: { opacity: 0, y: 80, scale: 0.7, filter: 'brightness(2)' },
      animate: { opacity: 1, y: 0, scale: [0.7, 1.08, 1], filter: 'brightness(1)' },
      transition: { duration: 0.8, type: 'spring', stiffness: 100, damping: 13 },
    },
    escudos: {
      initial: { opacity: 0, rotateY: 120, scale: 0.8 },
      animate: { opacity: 1, rotateY: 0, scale: 1 },
      transition: { duration: 0.6, ease: 'easeOut' },
    },
    prime: {
      initial: { opacity: 0, scale: 0.2, rotateY: 540, filter: 'brightness(3)' },
      animate: { opacity: 1, scale: 1, rotateY: 0, filter: 'brightness(1)' },
      transition: { duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.5 },
    },
    superprime: {
      initial: { opacity: 0, scale: 0, rotateY: 900, rotateZ: -60, filter: 'brightness(4) blur(8px)' },
      animate: { opacity: 1, scale: [0, 1.25, 1], rotateY: 0, rotateZ: 0, filter: 'brightness(1) blur(0px)' },
      transition: { duration: 1.9, ease: [0.16, 1, 0.3, 1], delay: 0.85 },
    },
  };

  const v = variants[type];

  return (
    <motion.div
      className={`relative ${size} [transform-style:preserve-3d]`}
      initial={v.initial}
      animate={v.animate}
      transition={v.transition}
      style={{
        filter: isSuper
          ? 'drop-shadow(0 0 60px rgba(165,243,252,1))'
          : isPrime
            ? 'drop-shadow(0 0 40px rgba(232,121,249,0.9))'
            : `drop-shadow(0 0 ${rarity * 6}px ${JARTICARD_STYLES[type].glow})`,
      }}
    >
      {/* Halo flotante continuo para rarezas altas */}
      {rarity >= 4 && (
        <motion.div
          className="pointer-events-none absolute -inset-4 rounded-3xl"
          animate={{ opacity: [0.4, 0.9, 0.4], scale: [1, 1.06, 1] }}
          transition={{ repeat: Infinity, duration: isSuper ? 1.1 : isPrime ? 1.4 : 2 }}
          style={{ background: `radial-gradient(circle, ${JARTICARD_STYLES[type].glow}66, transparent 70%)` }}
        />
      )}
      <motion.div
        className={`relative rounded-xl ${isSuper ? 'overflow-visible' : 'overflow-hidden'}`}
        animate={rarity >= 4 ? { y: [0, -8, 0] } : {}}
        transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
      >
        <JarticardFrame card={card} owned />
        {isPrime && <span className="jc-holo-overlay z-40" />}
      </motion.div>
    </motion.div>
  );
}

export default SobreReveal;
